import { z } from "zod";

import {
  seoGeoFields,
  systemFields,
  zCastMember,
  zDate,
  zGenreRef,
  zMedia,
  zTinaStamp,
  zTranslations,
  zTransliterations,
} from "@/lib/zod/show-fragments";

/** Read-only view schema for the Performances show page — full PerformanceDbDto. */
export const performanceViewSchema = z
  .object({
    id: z.string(),
    playId: z.string().nullish(),
    venueId: z.string().nullish(),
    play: z
      .object({
        id: z.string().nullish(),
        title: z.string().nullish(),
        slug: z.string().nullish(),
        media: zMedia.nullish(),
        genres: z.array(zGenreRef).nullish(),
      })
      .passthrough()
      .nullish(),
    venue: z
      .object({
        id: z.string().nullish(),
        name: z.string().nullish(),
        slug: z.string().nullish(),
        media: zMedia.nullish(),
        geolocation: z
          .object({ lat: z.number().nullish(), lng: z.number().nullish() })
          .passthrough()
          .nullish(),
        address: z.string().nullish(),
        area: z.string().nullish(),
        mapsUrl: z.string().nullish(),
        status: z.string().nullish(),
        timezone: z.string().nullish(),
      })
      .passthrough()
      .nullish(),
    startDate: zDate,
    endDate: zDate,
    cast: z.array(zCastMember).nullish(),
    status: z.string().nullish(),
    enrichmentStatus: z.string().nullish(),
    // `PerformanceAIEnrichmentDbDto`: the shared SEO/GEO contract only,
    // exactly like screening — no `overview`, no `reasoning`/`features`.
    enrichment: z
      .object({
        ...seoGeoFields,
        transliterations: zTransliterations.nullish(),
        translations: zTranslations,
      })
      .passthrough()
      .nullish(),
    ticketUrl: z.string().nullish(),
    // Set by Tina each time it reads this performance's price on the source page. Read-only.
    priceCheckedAt: zDate,
    pricing: z
      .array(
        z
          .object({
            label: z.string().nullish(),
            amount: z.number().nullish(),
            currency: z.string().nullish(),
          })
          .passthrough(),
      )
      .nullish(),
    screen: z
      .object({
        name: z.string().nullish(),
        type: z.string().nullish(),
      })
      .passthrough()
      .nullish(),
    tags: z.array(z.string()).nullish(),
    metadata: z
      .object({
        tina: zTinaStamp.nullish(),
      })
      .passthrough()
      .nullish(),
    hide: z.boolean().nullish(),
    ...systemFields,
  })
  .passthrough();

export type PerformanceViewType = z.infer<typeof performanceViewSchema>;
